import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';

// Status de acesso baseados no backend
// Regras:
// - ATIVO: Pagamento em dia
// - TRIAL: Periodo de teste
// - ATRASADO: 0-5 dias vencido (aviso) ou 5-15 dias (somente leitura)
// - BLOQUEADO_PARCIAL: 15-30 dias vencido (so Inicio, Config, Suporte)
// - BLOQUEADO: 30+ dias vencido (bloqueio total no login)
// - SEM_ASSINATURA: Cliente sem plano (mesmo que BLOQUEADO_PARCIAL)
const STATUS = {
  ATIVO: 'ATIVO',
  TRIAL: 'TRIAL',
  ATRASADO: 'ATRASADO',
  BLOQUEADO_PARCIAL: 'BLOQUEADO_PARCIAL',
  BLOQUEADO: 'BLOQUEADO',
  SEM_ASSINATURA: 'SEM_ASSINATURA',
  CANCELADO: 'CANCELADO'
};

const PaymentContext = createContext();

/**
 * Provider de pagamento que gerencia o status de faturamento do cliente
 * Controla acesso baseado em pagamentos em atraso
 */
export function PaymentProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCobranca, setSelectedCobranca] = useState(null);
  // Contador de erros consecutivos em /v1/fatura/status. Ate FAIL_OPEN_THRESHOLD
  // falhas, libera como ATIVO (UX - evita bloqueio por falha momentanea).
  // Acima disso, assume BLOQUEADO_PARCIAL (fail-closed). Reset em sucesso.
  //
  // Threshold = 3 (nao 2) por causa do React.StrictMode em dev: useEffect roda
  // 2x na primeira montagem (mount/unmount/mount), o que dispararia fail-closed
  // logo na entrada do app. Com 3 toleramos isso + o polling de 5min cobrir o
  // caso real de backend offline persistente.
  const FAIL_OPEN_THRESHOLD = 3;
  const consecutiveErrorsRef = React.useRef(0);

  /**
   * Carrega o status de pagamento do cliente
   * @param {boolean} isBackgroundRefresh - Se true, nao mostra loading (refresh silencioso)
   */
  const loadPaymentStatus = useCallback(async (isBackgroundRefresh = false) => {
    const isLogged = JSON.parse(localStorage.getItem('isLogged') || 'false');
    const token = localStorage.getItem('sessionToken');

    if (!isLogged || !token) {
      setStatus(null);
      setStatusData(null);
      setLoading(false);
      return;
    }

    // Apenas mostrar loading na carga inicial, nao em refreshes de background
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      const { data } = await api.get('/v1/fatura/status');

      setStatus(data.status);
      setStatusData(data);
      consecutiveErrorsRef.current = 0; // Sucesso zera contador

    } catch (err) {
      console.error('[PaymentContext] ERRO:', err.response?.status, err.message);

      // 402: backend bloqueou por inadimplencia - respeitar
      if (err.response?.status === 402) {
        const payload = err.response?.data || {};
        const statusFromBackend = payload.status || STATUS.BLOQUEADO;
        setStatus(statusFromBackend);
        setStatusData({
          status: statusFromBackend,
          permiteEdicao: false,
          mensagem: payload.erro || 'Acesso bloqueado por inadimplencia'
        });
        consecutiveErrorsRef.current = 0;
      }
      // 404: cliente sem assinatura
      else if (err.response?.status === 404) {
        setStatus(STATUS.SEM_ASSINATURA);
        setStatusData({
          status: STATUS.SEM_ASSINATURA,
          permiteEdicao: false,
          mensagem: 'Cliente sem plano ativo. Entre em contato para ativar.'
        });
        consecutiveErrorsRef.current = 0;
      }
      // Erro de rede/servidor: politica meio-termo
      else {
        setError(err.message);
        consecutiveErrorsRef.current += 1;
        if (consecutiveErrorsRef.current < FAIL_OPEN_THRESHOLD) {
          // Abaixo do threshold: libera como ATIVO (UX - evita bloqueio por
          // falha momentanea ou pelo duplo-disparo do React.StrictMode em dev)
          setStatus(STATUS.ATIVO);
          setStatusData({ status: STATUS.ATIVO, permiteEdicao: true });
        } else {
          // Acima do threshold: assume BLOQUEADO_PARCIAL (fail-closed)
          setStatus(STATUS.BLOQUEADO_PARCIAL);
          setStatusData({
            status: STATUS.BLOQUEADO_PARCIAL,
            permiteEdicao: false,
            mensagem: 'Nao foi possivel verificar status de pagamento. Acesso limitado ate reconexao.'
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Limpa os dados de pagamento (usado no logout)
   */
  const clearPaymentStatus = useCallback(() => {
    setStatus(null);
    setStatusData(null);
    setError(null);
    setLoading(true); // Resetar para estado inicial
  }, []);

  /**
   * Verifica se o acesso esta totalmente bloqueado (30+ dias de atraso)
   * Usuario nao pode nem fazer login
   */
  const isBlocked = useCallback(() => {
    return status === STATUS.BLOQUEADO;
  }, [status]);

  /**
   * Verifica se o acesso esta parcialmente bloqueado (15-30 dias ou sem assinatura)
   * Usuario so pode acessar Inicio, Configuracoes e Suporte
   */
  const isPartiallyBlocked = useCallback(() => {
    return status === STATUS.BLOQUEADO_PARCIAL || status === STATUS.SEM_ASSINATURA;
  }, [status]);

  /**
   * Verifica se a edicao esta bloqueada (5+ dias de atraso)
   */
  const isEditBlocked = useCallback(() => {
    if (!statusData) return false;
    return !statusData.permiteEdicao;
  }, [statusData]);

  /**
   * Verifica se esta em atraso (mas ainda com acesso)
   */
  const isOverdue = useCallback(() => {
    return status === STATUS.ATRASADO;
  }, [status]);

  /**
   * Verifica se esta em periodo de trial
   */
  const isTrial = useCallback(() => {
    return status === STATUS.TRIAL;
  }, [status]);

  /**
   * Verifica se o pagamento esta em dia
   */
  const isActive = useCallback(() => {
    return status === STATUS.ATIVO;
  }, [status]);

  /**
   * Verifica se a rota esta permitida no bloqueio parcial
   * Rotas permitidas: Inicio, Configuracoes, Suporte
   */
  const isRouteAllowed = useCallback((path) => {
    if (!isPartiallyBlocked()) return true;

    const allowedPaths = [
      '/',
      '/inicio',
      '/configuracoes',
      '/suporte',
      '/tickets',
      '/chat'
    ];

    // Verificar se o path comeca com alguma rota permitida
    return allowedPaths.some(allowed =>
      path === allowed || path.startsWith(`${allowed}/`)
    );
  }, [isPartiallyBlocked]);

  /**
   * Abre o modal de pagamento
   */
  const openPaymentModal = useCallback((cobranca = null) => {
    setSelectedCobranca(cobranca || statusData);
    setModalOpen(true);
  }, [statusData]);

  /**
   * Fecha o modal de pagamento
   */
  const closePaymentModal = useCallback(() => {
    setModalOpen(false);
    setSelectedCobranca(null);
  }, []);

  /**
   * Busca dados de PIX para uma cobranca
   */
  const fetchPixData = useCallback(async (idCobranca) => {
    const response = await api.get(`/v1/fatura/cobranca/${idCobranca}/pix`);
    return response.data;
  }, []);

  /**
   * Busca dados de boleto para uma cobranca
   */
  const fetchBoletoData = useCallback(async (idCobranca) => {
    const response = await api.get(`/v1/fatura/cobranca/${idCobranca}/boleto`);
    return response.data;
  }, []);

  /**
   * Busca extrato de cobrancas
   */
  const fetchExtrato = useCallback(async () => {
    const response = await api.get('/v1/fatura/extrato');
    return response.data;
  }, []);

  /**
   * Busca dados do plano
   */
  const fetchPlano = useCallback(async () => {
    const response = await api.get('/v1/fatura/plano');
    return response.data;
  }, []);

  // Carregar status ao montar e quando login mudar
  useEffect(() => {
    const isLogged = JSON.parse(localStorage.getItem('isLogged') || 'false');
    if (isLogged) {
      loadPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [loadPaymentStatus]);

  // Recarregar status periodicamente (a cada 5 minutos) - silenciosamente em background
  useEffect(() => {
    const isLogged = JSON.parse(localStorage.getItem('isLogged') || 'false');
    if (!isLogged) {
      return undefined;
    }

    const interval = setInterval(() => {
      // IMPORTANTE: passar true para isBackgroundRefresh para nao mostrar loading
      // e nao desmontar os componentes filhos (como modais abertos)
      loadPaymentStatus(true);
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(interval);
  }, [loadPaymentStatus]);

  const value = {
    // Estado
    status,
    statusData,
    loading,
    error,
    modalOpen,
    selectedCobranca,

    // Acoes
    loadPaymentStatus,
    clearPaymentStatus,
    openPaymentModal,
    closePaymentModal,

    // Verificacoes
    isBlocked,
    isPartiallyBlocked,
    isEditBlocked,
    isOverdue,
    isTrial,
    isActive,
    isRouteAllowed,

    // API
    fetchPixData,
    fetchBoletoData,
    fetchExtrato,
    fetchPlano,

    // Constantes
    STATUS
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
}

PaymentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook para acessar o contexto de pagamento
 */
export function usePayment() {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment deve ser usado dentro de um PaymentProvider');
  }
  return context;
}

export { STATUS };
export default PaymentContext;
